import { SCAN_STAGES, type ScanProgress } from "../core/primitives/scan-progress";
import type { BulkItem } from "../core/services/bulk-scan-queue";

function StageBar({ progress }: { progress: ScanProgress }) {
  return (
    <div className="mt-2 flex max-w-xs gap-1">
      {SCAN_STAGES.map(({ key, label }) => {
        // Done and in-flight share the accent, separated by the pulse rather
        // than by hue — a second colour here would read as a second meaning.
        const status = progress.stages[key];
        const tone =
          status === "complete"
            ? "bg-accent"
            : status === "active"
              ? "animate-pulse bg-accent"
              : status === "error"
                ? "bg-danger"
                : "bg-surface-strong";
        return <div key={key} title={label} className={`h-1 flex-1 rounded ${tone}`} />;
      })}
    </div>
  );
}

/**
 * A card still being scanned, shown at the top of the review queue.
 *
 * Deliberately the same shape as PendingScanRow — a scan in flight becomes a
 * pending one when it lands, and the row shouldn't visibly jump when it does.
 */
export function ScanningScanRow({
  item,
  onRetry,
  onRemove,
}: {
  item: BulkItem;
  onRetry: () => void;
  onRemove: () => void;
}) {
  const failed = item.status === "error";

  return (
    <div className="flex gap-3 rounded-row border border-line bg-surface p-4">
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
        <img src={item.previewUrl} alt="" className="h-full w-full object-cover" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-sm font-medium text-white" title={item.file.name}>
            {item.file.name}
          </p>
          <span
            className={`badge shrink-0 ${failed ? "badge-danger" : item.status === "scanning" ? "badge-warn" : "badge-neutral"}`}
          >
            {failed ? "Failed" : item.status === "scanning" ? "Scanning" : "Queued"}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          {item.backFile && <span className="text-xs text-faint">front + back</span>}
          {failed && (
            <span className="truncate text-xs text-danger" title={item.error ?? ""}>
              {item.error}
            </span>
          )}
        </div>
        {item.status === "scanning" && <StageBar progress={item.progress} />}
        {failed && (
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={onRetry} className="btn-primary btn-xs">
              Retry
            </button>
            <button type="button" onClick={onRemove} className="btn-ghost">
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
